import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useI18n } from '../i18n';
import { formatDate, formatNumber } from '../utils';

const formatReportData = (reportData, reportDates) => {
  const rows = [];
  const index = {};
  reportDates.forEach(date => {
    reportData[date].forEach(item => {
      let row = index[item.product_id];
      if (!row) {
        row = { id: item.product_id, productName: item.name, totalQuantity: 0, totalProfit: 0 };
        index[item.product_id] = row;
        rows.push(row);
      }
      row.totalQuantity += +item.total_quantity;
      row.totalProfit += +item.total_profit;
    });
  });

  rows.forEach(row => {
    reportDates.forEach(date => {
      const found = reportData[date]?.find(entry => entry.product_id === row.id);
      row[date] = {
        quantity: +(found?.total_quantity || 0),
        profit: +(found?.total_profit || 0),
      };
    });
  });

  const totals = { totalQuantity: 0, totalProfit: 0 };
  reportDates.forEach(date => {
    totals[date] = reportData[date].reduce(
      (acc, item) => ({
        quantity: acc.quantity + +item.total_quantity,
        profit: acc.profit + +item.total_profit,
      }),
      { quantity: 0, profit: 0 },
    );
    totals.totalQuantity += totals[date].quantity;
    totals.totalProfit += totals[date].profit;
  });

  const lineData = reportDates.map(date => ({ date, value: totals[date].profit }));

  const barData = [...rows]
    .sort((a, b) => b.totalProfit - a.totalProfit)
    .slice(0, 10)
    .map(el => ({ name: el.productName, value: el.totalProfit }));

  const pieData = rows
    .filter(el => el.totalQuantity > 0)
    .sort((a, b) => b.totalQuantity - a.totalQuantity)
    .map(el => ({ name: el.productName, value: el.totalQuantity }));

  return { data: rows, totals, lineData, barData, pieData };
};

export const useReportData = reportData => {
  const { isAdmin } = useSelector(state => state.authReducer);
  const { t } = useI18n();

  const reportDates = useMemo(
    () => (reportData && typeof reportData === 'object' ? Object.keys(reportData).sort() : []),
    [reportData],
  );

  const columns = useMemo(() => {
    const metricColumns = (getValue, keyPrefix, strong) => {
      const cols = [
        {
          key: `${keyPrefix}-qty`,
          title: t('reports.quantity'),
          align: 'right',
          width: 90,
          render: (_, row) => (
            <span className={strong ? 'wm-strong' : undefined}>{formatNumber(getValue(row).quantity)}</span>
          ),
          sorter: (a, b) => getValue(a).quantity - getValue(b).quantity,
        },
      ];
      if (isAdmin) {
        cols.push({
          key: `${keyPrefix}-profit`,
          title: t('reports.profit'),
          align: 'right',
          width: 130,
          render: (_, row) => (
            <span className={strong ? 'wm-strong' : undefined}>{formatNumber(getValue(row).profit)}</span>
          ),
          sorter: (a, b) => getValue(a).profit - getValue(b).profit,
        });
      }
      return cols;
    };

    return [
      {
        key: 'productName',
        title: t('reports.productName'),
        dataIndex: 'productName',
        width: 220,
        fixed: 'left',
        render: name => <span className='wm-strong'>{name}</span>,
        sorter: (a, b) => a.productName.localeCompare(b.productName),
      },
      ...reportDates.map(date => ({
        key: date,
        title: formatDate(date, 'D MMM, dd'),
        children: metricColumns(row => row[date], date),
      })),
      {
        key: 'overall',
        title: t('reports.overall'),
        fixed: 'right',
        children: metricColumns(
          row => ({ quantity: row.totalQuantity, profit: row.totalProfit }),
          'overall',
          true,
        ),
      },
    ];
  }, [isAdmin, reportDates, t]);

  const dataSource = useMemo(() => {
    if (!reportData) return { data: [], totals: null, lineData: [], barData: [], pieData: [] };
    return formatReportData(reportData, reportDates);
  }, [reportData, reportDates]);

  return { columns, reportDates, ...dataSource };
};
